/**
 * Shared shape for a Sealed "unified timeline" record: one row that starts
 * as a scheduled/upcoming item (a due date plus optional notes, no outcome
 * yet) or is created directly with a completed outcome (a walk-in visit
 * logged after the fact), and can later transition in place by having its
 * outcome logged — mirrors `domain/training/training-entry.ts`'s single-row
 * "planned entry + optional outcome fields" shape.
 *
 * Vet Records and Farrier Records (`docs/products/stable.md` "Vet Records
 * and Farrier Records") are both built on this shape today; Vaccinations
 * (same doc, next Sealed feature) is specified to reuse "the same unified-
 * timeline plumbing as Vet Records", so these constants/helpers are kept
 * generic rather than named after either feature.
 */
export const SEALED_RECORD_NOTES_MAX_LENGTH = 2000;
export const SEALED_RECORD_DOCUMENT_FILENAME_MAX_LENGTH = 180;
export const SEALED_RECORD_DOCUMENT_MAX_SIZE_BYTES = 20 * 1024 * 1024;
export const SEALED_RECORD_DOCUMENT_MAX_COUNT = 10;
/** Content types accepted for an attached document — a stamped passport
 * page, a lab report, or an invoice may arrive as a photo or a PDF (mirrors
 * the allow-list `packages/marketplace/src/public-validation.ts` uses for
 * its own private "vet document" uploads). */
export const SEALED_RECORD_DOCUMENT_CONTENT_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
];
const SEALED_RECORD_DOCUMENT_EXTENSIONS = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/webp": [".webp"],
};
export function matchesSealedRecordDocumentExtension(contentType, filename) {
    const extensions = SEALED_RECORD_DOCUMENT_EXTENSIONS[contentType];
    if (extensions === undefined)
        return false;
    const lower = filename.toLowerCase();
    return extensions.some((extension) => lower.endsWith(extension));
}
/**
 * The instant a Sealed timeline record sorts by for its unified-timeline
 * position: once an outcome is logged the record moves to when that
 * actually happened (`loggedAt`); a still-open scheduled entry sorts by its
 * `dueDate` (midnight UTC); a directly created entry with neither yet set
 * falls back to `createdAt`. Unlike Training entries — which always sort by
 * their fixed scheduled `day` — a Vet/Farrier record's position can move
 * once its outcome lands, since it may be logged well before or after its
 * due date.
 */
export function sealedRecordTimelineInstant(entry) {
    if (entry.loggedAt !== null)
        return entry.loggedAt;
    if (entry.dueDate !== null)
        return new Date(`${entry.dueDate}T00:00:00.000Z`);
    return entry.createdAt;
}
